// Chatbot con la API de Claude (VLT-382). El navegador JAMÁS ve la llave:
// el Worker (/api/ai/chat) llama a Anthropic con ANTHROPIC_API_KEY como
// secret y aplica la gobernanza (contexto mínimo — sin datos de clientes).
import { hasBackend, supabase } from '@/lib/supabase';
import i18n from '@/i18n';

export interface AiTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** Automatización explicable (VLT-399 · spec §17): qué herramienta usó
 *  Loruel y si funcionó — sin argumentos ni contenido, jamás retenido. */
export interface AiToolUse {
  name: string;
  ok: boolean;
}

export interface AiReply {
  text: string;
  toolsUsed: AiToolUse[];
  /** VLT-154: datos sensibles que el Worker ocultó (entrada, herramientas o respuesta). */
  masked?: number;
}

async function accessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export const aiService = {
  /** Conversación con Claude. null = no disponible (demo, sin configurar o
   *  error) — el copiloto cae a sus intents deterministas, jamás a silencio. */
  async chat(turns: AiTurn[]): Promise<AiReply | null> {
    if (!hasBackend) return null;
    const token = await accessToken();
    if (!token) return null;
    try {
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        // lang: el idioma del SISTEMA del usuario — Claude responde en él
        // aunque el mensaje sea ambiguo ("hola", "ok").
        body: JSON.stringify({ messages: turns, lang: i18n.language?.startsWith('es') ? 'es' : 'en' }),
      });
      // VLT-157: tope del plan lleno — el chat LO DICE con camino de upgrade
      // (silencio o fallback aquí sería mentirle al usuario sobre su plan).
      if (res.status === 402) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        if (err?.error === 'limite_ia') return { text: i18n.t('limits.ai'), toolsUsed: [] };
        return null;
      }
      if (!res.ok) return null;
      const out = (await res.json()) as { text?: string; refused?: boolean; tools_used?: AiToolUse[]; masked?: number };
      return out.text?.trim() ? { text: out.text, toolsUsed: out.tools_used ?? [], masked: out.masked ?? 0 } : null;
    } catch {
      return null;
    }
  },
};
